/**
 * WallTool.ts
 * Sims-style wall state cycling with reciprocal neighbor updates.
 *
 * When a wall is cycled on one cell, the shared wall on the adjacent
 * cell is automatically updated to match, maintaining grid consistency.
 *
 * Editor cycle order: SOLID → DOOR → GATE → GRATE → WINDOW → SECRET → OPEN
 * (OPEN is last because removing a wall is the most drastic action)
 */

import type { GridCell, WallState } from '../types/OctileTypes.ts'
import { setWall, getCell } from '../grid/OctileGrid.ts'
import { getCellType, getOctWallToward, getConnWallToward, OCT_WALL_NAMES } from '../grid/GridAddressing.ts'

// Editor-specific cycle: OPEN last (removing a wall is drastic)
const EDITOR_WALL_CYCLE: readonly WallState[] = [
    'SOLID', 'DOOR', 'GATE', 'GRATE', 'WINDOW', 'SECRET', 'OPEN'
] as const

// ─── Reciprocal Wall Mapping ────────────────────────────────

interface ReciprocalTarget {
    row: number
    col: number
    wallIndex: number
}

/**
 * Given a cell and wall index, find the neighbor cell and its
 * corresponding wall index that shares this wall segment.
 */
function getReciprocal(row: number, col: number, wallIndex: number): ReciprocalTarget | null {
    const kind = getCellType(row, col)

    if (kind === 'octagon') {
        // Octagon wall offsets: 0=N, 1=NE, 2=E, 3=SE, 4=S, 5=SW, 6=W, 7=NW
        const neighborOffsets: Array<{ dr: number; dc: number }> = [
            { dr: -2, dc: 0 },   // 0: N → octagon
            { dr: -1, dc: 1 },   // 1: NE → connector
            { dr: 0, dc: 2 },    // 2: E → octagon
            { dr: 1, dc: 1 },    // 3: SE → connector
            { dr: 2, dc: 0 },    // 4: S → octagon
            { dr: 1, dc: -1 },   // 5: SW → connector
            { dr: 0, dc: -2 },   // 6: W → octagon
            { dr: -1, dc: -1 },  // 7: NW → connector
        ]

        const offset = neighborOffsets[wallIndex]
        if (!offset) return null

        const nRow = row + offset.dr
        const nCol = col + offset.dc
        const nKind = getCellType(nRow, nCol)

        let nWallIndex: number
        if (nKind === 'octagon') {
            nWallIndex = getOctWallToward(nRow, nCol, row, col)
        } else if (nKind === 'connector') {
            nWallIndex = getConnWallToward(nRow, nCol, row, col)
        } else {
            return null
        }

        if (nWallIndex === -1) return null
        return { row: nRow, col: nCol, wallIndex: nWallIndex }
    }

    if (kind === 'connector') {
        // Connector wall offsets: 0=TL, 1=TR, 2=BR, 3=BL
        const neighborOffsets: Array<{ dr: number; dc: number }> = [
            { dr: -1, dc: -1 },  // 0: TL → octagon
            { dr: -1, dc: 1 },   // 1: TR → octagon
            { dr: 1, dc: 1 },    // 2: BR → octagon
            { dr: 1, dc: -1 },   // 3: BL → octagon
        ]

        const offset = neighborOffsets[wallIndex]
        if (!offset) return null

        const nRow = row + offset.dr
        const nCol = col + offset.dc
        const nWallIndex = getOctWallToward(nRow, nCol, row, col)

        if (nWallIndex === -1) return null
        return { row: nRow, col: nCol, wallIndex: nWallIndex }
    }

    return null
}

// ─── Wall Tool ──────────────────────────────────────────────

export class WallTool {
    private grid: GridCell[][]
    private gridWidth: number
    private gridHeight: number

    constructor(grid: GridCell[][], gridWidth: number, gridHeight: number) {
        this.grid = grid
        this.gridWidth = gridWidth
        this.gridHeight = gridHeight
    }

    /**
     * Cycle a wall to the next state (editor order) and update reciprocal.
     * Returns { newState, reciprocal } so the caller can update visuals.
     */
    cycleWall(row: number, col: number, wallIndex: number): {
        newState: WallState
        reciprocal: ReciprocalTarget | null
    } | null {
        const cell = getCell(this.grid, row, col)
        if (!cell) return null
        if (wallIndex < 0 || wallIndex >= cell.walls.length) return null

        // Cycle using editor order (OPEN last)
        const current = cell.walls[wallIndex]
        const idx = EDITOR_WALL_CYCLE.indexOf(current)
        const newState = EDITOR_WALL_CYCLE[(idx + 1) % EDITOR_WALL_CYCLE.length]

        // Set on this cell
        setWall(this.grid, row, col, wallIndex, newState)

        // Update reciprocal wall on the neighbor
        const reciprocal = getReciprocal(row, col, wallIndex)
        if (reciprocal) {
            const nCell = getCell(this.grid, reciprocal.row, reciprocal.col)
            if (nCell?.active) {
                setWall(this.grid, reciprocal.row, reciprocal.col, reciprocal.wallIndex, newState)
            }
        }

        return { newState, reciprocal }
    }

    /**
     * Set a wall to a specific state (no cycling) and update reciprocal.
     * Returns { reciprocal } so the caller can update visuals.
     */
    setWallState(row: number, col: number, wallIndex: number, state: WallState): {
        reciprocal: ReciprocalTarget | null
    } | null {
        const cell = getCell(this.grid, row, col)
        if (!cell) return null
        if (wallIndex < 0 || wallIndex >= cell.walls.length) return null

        setWall(this.grid, row, col, wallIndex, state)

        const reciprocal = getReciprocal(row, col, wallIndex)
        if (reciprocal) {
            const nCell = getCell(this.grid, reciprocal.row, reciprocal.col)
            if (nCell?.active) {
                setWall(this.grid, reciprocal.row, reciprocal.col, reciprocal.wallIndex, state)
            }
        }

        return { reciprocal }
    }

    /**
     * Update grid reference (after regeneration).
     */
    setGrid(grid: GridCell[][], gridWidth: number, gridHeight: number): void {
        this.grid = grid
        this.gridWidth = gridWidth
        this.gridHeight = gridHeight
    }

    /**
     * Get human-readable wall direction name.
     */
    static getWallName(row: number, col: number, wallIndex: number): string {
        const kind = getCellType(row, col)
        if (kind === 'octagon') {
            return OCT_WALL_NAMES[wallIndex] ?? `wall ${wallIndex}`
        }
        const connNames = ['TL', 'TR', 'BR', 'BL']
        return connNames[wallIndex] ?? `wall ${wallIndex}`
    }
}

// Export for testing
export { getReciprocal, EDITOR_WALL_CYCLE }
